import { useEffect, useRef, useCallback } from 'react';
import { useUser } from '@/lib/context/user-context';

interface HistoryItem {
  episodeId: string;
  episodeTitle: string;
  podcastTitle?: string;
  thumbnailUrl?: string;
  description?: string;  // 🔥 新增：episode描述
  ownerAvatarUrl?: string; // 🔥 新增：作者/频道头像
  userPlaybackPosition: number;
  mediaFileDuration: number;
  completed?: boolean;
}

export function useEpisodeHistory(
  episodeId: string,
  episodeTitle: string,
  podcastTitle?: string,
  thumbnailUrl?: string,
  description?: string,  // 🔥 新增：description参数
  ownerAvatarUrl?: string, // 🔥 新增：ownerAvatarUrl参数
  mediaFileDuration?: number
) {
  const { user } = useUser();
  const userRef = useRef(user);
  userRef.current = user; // 始终保持最新的user引用
  
  // 🔥 关键修复：使用ref保存最新的episodeId，避免闭包陈旧问题
  const episodeIdRef = useRef(episodeId);
  const episodeTitleRef = useRef(episodeTitle);
  const podcastTitleRef = useRef(podcastTitle);
  const descriptionRef = useRef(description);
  const ownerAvatarUrlRef = useRef(ownerAvatarUrl);
  
  // 更新refs以保持最新值
  episodeIdRef.current = episodeId;
  episodeTitleRef.current = episodeTitle;
  podcastTitleRef.current = podcastTitle;
  descriptionRef.current = description;
  ownerAvatarUrlRef.current = ownerAvatarUrl;
  
  const lastUpdateTimeRef = useRef<number>(0);
  const isUpdatingRef = useRef<boolean>(false);
  
  // 调试：hook参数变化
  useEffect(() => {
    console.log('📌 useEpisodeHistory parameters changed:', {
      episodeId,
      episodeTitle: episodeTitle?.substring(0, 50),
      hasUser: !!user,
      userRefHasUser: !!userRef.current,
      ownerAvatarUrl: !!ownerAvatarUrl
    });
  }, [episodeId, episodeTitle, description, ownerAvatarUrl, user]);

  // Immediate update function with simple duplicate prevention
  const updateHistory = useCallback(
    async (position: number, duration?: number, completed?: boolean) => {
      // 🔥 关键修复：使用ref中的最新值，而不是闭包中的旧值
      const currentEpisodeId = episodeIdRef.current;
      const currentEpisodeTitle = episodeTitleRef.current;
      const currentPodcastTitle = podcastTitleRef.current;
      const currentDescription = descriptionRef.current;
      const currentOwnerAvatarUrl = ownerAvatarUrlRef.current;
      
      if (!userRef.current || !currentEpisodeId || !currentEpisodeTitle) {
        if (!userRef.current) console.log('❌ History update skipped: No user');
        if (!currentEpisodeId) console.log('❌ History update skipped: No episodeId');
        if (!currentEpisodeTitle) console.log('❌ History update skipped: No episodeTitle');
        return;
      }
      
      // Prevent duplicate updates
      if (isUpdatingRef.current) return;
      
      const actualDuration = duration || mediaFileDuration || 0;
      if (actualDuration <= 0) return;

      // 🎯 Podverse风格：完全禁用自动5秒保存，只在关键时刻保存
      // 如果不是完成标记，且不是立即保存调用，则跳过
      if (!completed) {
        // console.log('🚫 Auto-save disabled, use saveProgressImmediately instead');
        return;
      }

      try {
        isUpdatingRef.current = true;
        lastUpdateTimeRef.current = position;

        const historyData: HistoryItem = {
          episodeId: currentEpisodeId,
          episodeTitle: currentEpisodeTitle,
          podcastTitle: currentPodcastTitle,
          thumbnailUrl,
          description: currentDescription,
          ownerAvatarUrl: currentOwnerAvatarUrl,
          userPlaybackPosition: Math.round(position),
          mediaFileDuration: Math.round(actualDuration),
          completed: completed || position >= actualDuration * 0.95 // Auto-complete at 95%
        };

        const response = await fetch(`/api/user/${userRef.current.id}/history`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(historyData)
        });

        if (!response.ok) {
          console.error('Failed to update history:', response.statusText);
        } else {
          console.log('✅ History updated immediately:', {
            episodeId: currentEpisodeId,
            position: Math.round(position),
            duration: actualDuration
          });
          // 🔥 触发自定义事件，通知其他组件历史记录已更新
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new Event('history-updated'));
          }
        }
      } catch (error) {
        console.error('Error updating episode history:', error);
      } finally {
        isUpdatingRef.current = false;
      }
    },
    [episodeId, episodeTitle, podcastTitle, thumbnailUrl, description, ownerAvatarUrl, mediaFileDuration]
  );

  // Track playback position
  const trackProgress = useCallback(
    (currentTime: number, duration?: number) => {
      // 🔥 关键修复：在调用时获取最新的episodeId，避免闭包陈旧问题
      // const currentEpisodeId = episodeIdRef.current;
      // const currentEpisodeTitle = episodeTitleRef.current;
      
      // 调试日志 - 降低频率避免刷屏
      if (Math.floor(currentTime) % 15 === 0) { // 每15秒记录一次
        // console.log('🔍 trackProgress called:', {
        //   episodeId: currentEpisodeId,
        //   episodeTitle: currentEpisodeTitle?.substring(0, 50),
        //   currentTime: Math.round(currentTime),
        //   duration: Math.round(duration || mediaFileDuration || 0),
        //   hasUser: !!userRef.current
        // });
      }
      
      if (currentTime > 0 && (duration || mediaFileDuration)) {
        updateHistory(currentTime, duration);
      }
    },
    [updateHistory, mediaFileDuration] // 🔥 移除episodeId和episodeTitle依赖，因为现在使用ref
  );

  // 🎯 新增：立即保存进度（类似 Podverse 方案）
  const saveProgressImmediately = useCallback(
    (currentTime: number, duration?: number, reason?: string) => {
      if (typeof window !== 'undefined') {
        const flag = (window as any).__PLAYER_SKIP_HISTORY_REPLAY__;
        const timeout = (window as any).__PLAYER_SKIP_HISTORY_REPLAY_TIMEOUT__;
        if (flag) {
          if (typeof timeout === 'number' && timeout < Date.now()) {
            delete (window as any).__PLAYER_SKIP_HISTORY_REPLAY__;
            delete (window as any).__PLAYER_SKIP_HISTORY_REPLAY_TIMEOUT__;
          } else {
            return;
          }
        }
      }

      const currentEpisodeId = episodeIdRef.current;
      const currentEpisodeTitle = episodeTitleRef.current;
      
      if (!userRef.current || !currentEpisodeId || !currentEpisodeTitle) {
        console.log('❌ Immediate save skipped: Missing required data');
        return;
      }

      if ((currentTime > 0 || reason === 'completed' || reason === 'finished') && (duration || mediaFileDuration)) {
        // console.log('💾 Immediate save triggered:', {
        //   reason: reason || 'manual',
        //   episodeId: currentEpisodeId,
        //   position: Math.round(currentTime),
        //   duration: Math.round(duration || mediaFileDuration || 0)
        // });
        
        // 绕过5秒限制，强制保存
        const actualDuration = duration || mediaFileDuration || 0;
        if (actualDuration <= 0) {
          return;
        }

        const isCompletion = reason === 'completed' || reason === 'finished';
        const resumePosition = isCompletion ? 0 : Math.round(currentTime);
        const completedFlag = isCompletion || resumePosition >= actualDuration * 0.95;

        const historyData = {
          episodeId: currentEpisodeId,
          episodeTitle: currentEpisodeTitle,
          podcastTitle: podcastTitleRef.current,
          thumbnailUrl,
          description: descriptionRef.current,
          ownerAvatarUrl: ownerAvatarUrlRef.current, // 🔥 新增：发送ownerAvatarUrl
          userPlaybackPosition: resumePosition,
          mediaFileDuration: Math.round(actualDuration),
          completed: completedFlag
        };

        // 直接调用 API，不经过 updateHistory 的5秒限制
        fetch(`/api/user/${userRef.current.id}/history`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(historyData)
        }).then(response => {
          if (response.ok) {
            console.log('✅ Immediate save successful');
            // 更新最后保存时间，避免后续重复保存
            lastUpdateTimeRef.current = resumePosition;
          } else {
            console.error('❌ Immediate save failed:', response.statusText);
          }
        }).catch(error => {
          console.error('❌ Immediate save error:', error);
        });
      }
    },
    [mediaFileDuration, thumbnailUrl, description, ownerAvatarUrl]
  );

  // Mark episode as completed
  const markCompleted = useCallback(
    (finalPosition?: number, duration?: number) => {
      const actualDuration = duration || mediaFileDuration || finalPosition || lastUpdateTimeRef.current;
      if (!actualDuration) {
        return;
      }
      saveProgressImmediately(actualDuration, actualDuration, 'completed');
    },
    [saveProgressImmediately, mediaFileDuration]
  );

  // Get user's last position for this episode
  const getLastPosition = useCallback(async (): Promise<number> => {
    const currentEpisodeId = episodeIdRef.current;
    if (!userRef.current || !currentEpisodeId) return 0;

    try {
      const response = await fetch(`/api/user/${userRef.current.id}/history`);
      if (!response.ok) return 0;

      const data = await response.json();
      const historyItem = data.historyItems?.find((item: any) => item.episodeId === currentEpisodeId);
      if (historyItem) {
        console.log('🧭 Found resume history item', {
          episodeId: currentEpisodeId,
          resumePosition: historyItem.userPlaybackPosition,
          duration: historyItem.mediaFileDuration,
          completed: historyItem.completed,
        });
      } else {
        console.log('🧭 No history item found for episode', currentEpisodeId);
      }
      
      if (!historyItem) {
        return 0;
      }
      if (historyItem.completed) {
        return 0;
      }
      return historyItem.userPlaybackPosition || 0;
    } catch (error) {
      console.error('Error fetching last position:', error);
      return 0;
    }
  }, []); // 🔥 移除episodeId依赖，因为现在使用ref

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      // No timeout cleanup needed since we use immediate saves
    };
  }, []);

  return {
    trackProgress,
    saveProgressImmediately, // 🎯 新增：立即保存功能
    markCompleted,
    getLastPosition
  };
}
